import { Helmet } from "react-helmet";
import { useState } from "react";
import {
  FaDatabase,
  FaDollarSign,
  FaGaugeHigh,
  FaTag,
  FaArrowTrendUp,
  FaUser,
  FaPenToSquare,
  FaMagnifyingGlass,
  FaPlus,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { Header } from "../../../../components/Header";
import { useHookPppoeProfile } from "../../../../hooks/useHookPppoeProfile";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { useHookHotspotProfile } from "../../../../hooks/useHookHotspotProfile";
import { HotspotProfileCreate } from "./create";

/* ============================================
   Skeleton Loading
   ============================================ */
const SkeletonRow = ({ cols = 7 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div
          className="h-3.5 bg-slate-700/50 rounded-md"
          style={{ width: `${35 + Math.random() * 55}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl p-5 animate-pulse"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
    }}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-700/50" />
        <div className="space-y-2">
          <div className="h-4 bg-slate-700/50 rounded-md w-28" />
          <div className="h-3 bg-slate-700/30 rounded-md w-16" />
        </div>
      </div>
      <div className="h-6 bg-slate-700/30 rounded-full w-16" />
    </div>
    <div className="space-y-3 pt-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
      <div className="h-12 bg-slate-700/30 rounded-xl" />
      <div className="grid grid-cols-2 gap-3">
        <div className="h-14 bg-slate-700/25 rounded-xl" />
        <div className="h-14 bg-slate-700/25 rounded-xl" />
      </div>
    </div>
  </div>
);

/* ============================================
   Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "aktif";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${isActive
          ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
          : "bg-red-500/12 text-red-400 border border-red-500/20"
        }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-red-400"}`} />
      {status}
    </span>
  );
};

/* ============================================
   Format Rupiah
   ============================================ */
const formatRupiah = (value) => {
  if (!value && value !== 0) return "-";
  return `Rp ${Number(value).toLocaleString("id-ID")}`;
};

/* ============================================
   KOMPONEN: PppoeProfile
   ============================================ */
export const HotspotProfile = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);

  const { data, loading, refreshData, keyword, setKeyword } = useHookHotspotProfile();

  /* ---------- Table Columns ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    // { key: "group", label: "Group", align: "left" },
    { key: "name", label: "Nama Profile", align: "left" },
    { key: "price", label: "Harga Jual", align: "left" },
    { key: "reseller_fee", label: "Reseller Fee", align: "left" },
    { key: "rate_limit", label: "Rate Limit", align: "left" },
    { key: "status", label: "Status", align: "left", width: "120px" },
  ];

  return (
    <>
      <Helmet>
        <title>PPPoE Profile - Radius Trend</title>
        <meta
          name="description"
          content="Kelola data PPPoE profile — atur harga jual, reseller fee, rate limit, dan status profile pelanggan ISP."
        />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <h2 className="text-base md:text-xl font-display font-bold text-gray-100">
            Management PPPoE Profile
          </h2>
          <BtnDefault onClick={() => setOpenModalCreate(true)}>
            <FaPlus className="text-xs hidden sm:block" />
            <span>Tambah Profile</span>
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search ===== */}
          <section
            className="rounded-2xl p-4"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Pencarian"
          >
            <div className="w-full max-w-sm">
              <div className="relative">
                <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                <input
                  type="text"
                  placeholder="Cari nama profile..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                />
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel PPPoE profile"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {/* Head */}
                <thead>
                  <tr
                    className="border-b"
                    style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"}`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                  {/* Loading */}
                  {loading && !data && (
                    <>
                      {[...Array(5)].map((_, i) => <SkeletonRow key={i} cols={columns.length} />)}
                    </>
                  )}

                  {/* Empty */}
                  {!loading && data?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                          >
                            <FaDatabase className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">Tidak ada profile ditemukan</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {keyword ? 'Coba ubah kata kunci pencarian' : 'Belum ada PPPoE profile yang terdaftar'}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Data Rows */}
                  {!loading &&
                    data?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150"
                        style={{ background: "transparent" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        {/* No */}
                        <td className="px-5 py-4 text-center text-slate-500 font-mono text-xs">
                          {index + 1}
                        </td>

                        {/* Group */}
                        {/* <td className="px-5 py-4 text-slate-400 text-xs">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-700/40 border border-slate-600/25 font-medium">
                            <FaTag className="text-[10px] text-slate-500" />
                            {item.group?.name || "—"}
                          </span>
                        </td> */}

                        {/* Nama */}
                        <td className="px-5 py-4">
                          <span className="font-semibold text-gray-100">{item.name}</span>
                        </td>

                        {/* Harga Jual */}
                        <td className="px-5 py-4">
                          <span className="font-mono text-sm text-gray-200 bg-cyan-500/8 px-2.5 py-1 rounded-lg border border-cyan-500/10">
                            {formatRupiah(item.price)}
                          </span>
                        </td>

                        {/* Reseller Fee */}
                        <td className="px-5 py-4">
                          <span className="font-mono text-sm text-emerald-400 bg-emerald-500/8 px-2.5 py-1 rounded-lg border border-emerald-500/10">
                            {formatRupiah(item.reseller_fee)}
                          </span>
                        </td>

                        {/* Rate Limit */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <FaGaugeHigh className="text-xs text-slate-500" />
                            <span className="text-slate-300 text-sm">
                              {item.rate_limit || "Unlimited"}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-5 py-4">
                          <StatusBadge status={item.status} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== Mobile Card List ===== */}
          <section className="lg:hidden space-y-3" aria-label="Daftar PPPoE profile">
            {/* Loading */}
            {loading && !data && (
              <>
                {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
              </>
            )}

            {/* Empty */}
            {!loading && data?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                >
                  <FaDatabase className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">Tidak ada profile ditemukan</p>
                <p className="text-xs text-slate-500 mt-1">
                  {keyword ? "Coba ubah pencarian" : "Belum ada data PPPoE profile"}
                </p>
              </div>
            )}

            {/* Cards */}
            {!loading &&
              data?.map((item) => (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden transition-all duration-200"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(0, 180, 216, 0.15)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.5)";
                  }}
                >
                  {/* Card Header */}
                  <div
                    className="p-4 flex justify-between items-center"
                    style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.15)" }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{
                          background: "linear-gradient(135deg, rgba(0,180,216,0.15), rgba(0,119,182,0.08))",
                          border: "1px solid rgba(0,180,216,0.15)",
                        }}
                      >
                        <FaDatabase className="text-sm text-ocean-500" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-100 leading-tight text-sm truncate">
                          {item.name}
                        </h3>
                        <p className="text-[10px] text-ocean-400 font-semibold uppercase tracking-wider flex items-center gap-1 mt-0.5">
                          <FaTag className="text-[8px]" />
                          {item.group?.name || "No Group"}
                        </p>
                      </div>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-3">
                    {/* Rate Limit */}
                    <div
                      className="flex items-center gap-3 p-3 rounded-xl"
                      style={{ background: "rgba(0, 180, 216, 0.05)", border: "1px solid rgba(0, 180, 216, 0.1)" }}
                    >
                      <FaGaugeHigh className="text-ocean-500 text-sm flex-shrink-0" />
                      <div>
                        <span className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider block">
                          Rate Limit
                        </span>
                        <span className="text-sm font-bold text-gray-100 block mt-0.5">
                          {item.rate_limit || "Unlimited"}
                        </span>
                      </div>
                    </div>

                    {/* Price Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Harga Jual */}
                      <div
                        className="p-3 rounded-xl"
                        style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.25)" }}
                      >
                        <div className="flex items-center gap-1.5 text-slate-500 mb-1.5">
                          <FaDollarSign className="text-xs" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Harga Jual</span>
                        </div>
                        <span className="text-sm font-bold text-gray-100 font-mono">
                          {formatRupiah(item.price)}
                        </span>
                      </div>

                      {/* Reseller Fee */}
                      <div
                        className="p-3 rounded-xl"
                        style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.25)" }}
                      >
                        <div className="flex items-center gap-1.5 text-slate-500 mb-1.5">
                          <FaArrowTrendUp className="text-xs" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Fee Reseller</span>
                        </div>
                        <span className="text-sm font-bold text-emerald-400 font-mono">
                          {formatRupiah(item.reseller_fee)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div
                    className="px-4 py-3 flex justify-end"
                    style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
                  >
                    <button
                      className="flex items-center gap-2 text-sm font-semibold text-ocean-400 hover:text-ocean-300 px-4 py-2 rounded-xl transition-all duration-200 hover:bg-ocean-600/8"
                    >
                      <FaPenToSquare className="text-xs" />
                      Detail / Edit
                    </button>
                  </div>
                </article>
              ))}
          </section>
        </main>
      </DashboardLayout>

      {/* ===== Modal Create ===== */}
      <HotspotProfileCreate
        openModal={openModalCreate}
        setOpenModal={setOpenModalCreate}
        refresh={refreshData}
      />
    </>
  );
};